/**
 * A free-text search value backed by a URL search param.
 *
 * Keeping the term on the URL (rather than component `useState`) means it
 * survives anything that remounts the input: switching hub tabs (which
 * unmounts the previous tab), opening/closing a modal that re-renders the
 * surface, and browser refresh / deep-link. Sibling surfaces that read the
 * SAME param key share one filter, so a name typed on one tab carries to the
 * next.
 *
 * Returns `[value, setValue]`:
 *  - `value` is a local mirror updated synchronously, so typing is instant and
 *    the caret never jumps.
 *  - `setValue` updates the mirror AND writes the URL param with `replace:
 *    true` — no debounce, so a fast tab-switch mid-type can't drop the last
 *    keystrokes, and `replace` keeps keystrokes out of browser history. An
 *    empty value drops the key so the URL stays clean.
 *  - When the URL param changes from outside this input (deep-link,
 *    back/forward, a tab-switch remount), the mirror re-syncs.
 *
 * Fetch debouncing stays with the caller (e.g. `useDebounce(value, ms)` in the
 * query key), so writing the URL eagerly does not add network chatter — the
 * URL write is cheap and the request is still gated.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

type RoutePath = '/hr/leaves'

interface UseUrlSearchOptions {
  /** The current route path — the target the URL write navigates to. */
  to: RoutePath
  /** URL param key. Defaults to `q`. Sibling surfaces sharing a key share the filter. */
  key?: string
}

export function useUrlSearch({
  to,
  key = 'q',
}: UseUrlSearchOptions): [string, (next: string) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const navigate = useNavigate()

  const urlValue = typeof search[key] === 'string' ? (search[key] as string) : ''
  const [value, setValue] = useState(urlValue)

  // Track the value we last wrote so an external URL change (deep-link,
  // back/forward, remount) re-syncs the mirror, while our own write does not
  // clobber the input mid-keystroke.
  const lastWritten = useRef(urlValue)
  useEffect(() => {
    if (urlValue !== lastWritten.current) {
      lastWritten.current = urlValue
      setValue(urlValue)
    }
  }, [urlValue])

  const set = useCallback(
    (next: string): void => {
      setValue(next)
      lastWritten.current = next
      void navigate({
        to,
        search: (prev: Record<string, unknown>) => {
          const out: Record<string, string> = {}
          for (const [k, v] of Object.entries(prev)) {
            if (typeof v === 'string' && v !== '') out[k] = v
          }
          if (next === '') delete out[key]
          else out[key] = next
          return out
        },
        replace: true,
      })
    },
    [navigate, to, key],
  )

  return [value, set]
}
